/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { FeatureFlags } from "@/feature_flags/feature_flag";
import { useFlag } from "@openfeature/react-sdk";
import { ComingSoon } from "@/components/coming-soon";
import {
  GeofenceGeometry,
  GeofenceType,
  GeofencePolygonGeometry,
} from "@/api/v1/geofence-types";
import { FlightPlanForm } from "@/components/flight-plan/flight-plan-form";
import { CreateFlightPlanInput } from "@/api/v1/flight-plan-types";
import { useCreateFlightPlanMutation } from "@/api/v1/flight-plan-api";
import { GeofenceDraw } from "@/components/map/geofence-draw";
import { Map } from "react-map-gl/maplibre";
import { useStore } from "@/store/store";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { ArrowLeft, MapPin, CheckCircle, AlertTriangle, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { FlightRestrictionsLayer } from "@/components/map/flight-restrictions-layer";
import * as turf from "@turf/turf";
import { useMemo } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const FlightPlanSubmissionRoute = () => {
  const { value: isFlightPlanEnabled } = useFlag(
    FeatureFlags.flight_plan_submission_enabled,
    false
  );

  const navigate = useNavigate();
  const [step, setStep] = useState<"draw" | "details">("draw");
  const [geofenceGeometry, setGeofenceGeometry] =
    useState<GeofenceGeometry | null>(null);
  const [showFlightRestrictions, setShowFlightRestrictions] = useState(true);

  const mapStyleUrl = useStore((state) => state.mapStyleUrl);
  const initialViewState = useStore((state) => state.mapBounds);
  const zoom = useStore((state) => state.mapZoom);

  const createFlightPlanMutation = useCreateFlightPlanMutation();

  // Calculate polygon area in km²
  const calculatePolygonAreaKm2 = useCallback((polygon: GeofencePolygonGeometry | null): number => {
    if (!polygon || !polygon.coordinates || polygon.coordinates.length === 0 || !polygon.coordinates[0] || polygon.coordinates[0].length < 3) {
      return 0;
    }
    
    try {
      const geoJsonPolygon: GeoJSON.Polygon = {
        type: "Polygon",
        coordinates: polygon.coordinates,
      };
      
      const areaM2 = turf.area(geoJsonPolygon);
      return areaM2 / 1000000; // Convert m² to km²
    } catch (error) {
      console.error("Error calculating polygon area:", error);
      return 0;
    }
  }, []);

  const polygonAreaKm2 = useMemo(() => {
    if (!geofenceGeometry) return 0;
    return calculatePolygonAreaKm2(geofenceGeometry as GeofencePolygonGeometry);
  }, [geofenceGeometry, calculatePolygonAreaKm2]);

  const polygonPointCount = useMemo(() => {
    if (!geofenceGeometry) return 0;
    const polygonGeometry = geofenceGeometry as GeofencePolygonGeometry;
    return polygonGeometry.coordinates?.[0]?.length || 0;
  }, [geofenceGeometry]);

  const isPolygonValid = useMemo(() => {
    if (!geofenceGeometry) return false;
    const polygonGeometry = geofenceGeometry as GeofencePolygonGeometry;
    
    // Check minimum points
    if (!polygonGeometry.coordinates || polygonGeometry.coordinates.length === 0 || polygonGeometry.coordinates[0].length < 4) {
      return false;
    }
    
    // Check area
    if (polygonAreaKm2 === 0 || polygonAreaKm2 > 1000) {
      return false;
    }
    
    return true;
  }, [geofenceGeometry, polygonAreaKm2]);

  const handleGeofenceDrawn = useCallback(
    (geometry: GeofenceGeometry, _type: GeofenceType) => {
      if (_type === "circle") {
        toast.error("Flight plans must use polygon areas, not circles.");
        return;
      }
      setGeofenceGeometry(geometry);
    },
    []
  );

  const handleContinue = () => {
    if (!geofenceGeometry) return;

    const polygonGeometry = geofenceGeometry as GeofencePolygonGeometry;
    
    // Validate minimum points
    if (
      !polygonGeometry.coordinates ||
      polygonGeometry.coordinates.length === 0 ||
      polygonGeometry.coordinates[0].length < 4
    ) {
      toast.error("Please draw a valid polygon with at least 3 points");
      return;
    }

    // Validate polygon area
    const area = calculatePolygonAreaKm2(polygonGeometry);
    if (area === 0) {
      toast.error("Polygon area is invalid. Please redraw the area.");
      return;
    }
    
    if (area > 1000) {
      toast.error(`Polygon area (${area.toFixed(2)} km²) exceeds the maximum of 1000 km². Please draw a smaller area.`);
      return;
    }

    setStep("details");
  };

  const handleSubmit = async (data: CreateFlightPlanInput) => {
    try {
      const result = await createFlightPlanMutation.mutateAsync(data);
      toast.success("Flight plan submitted successfully");
      // Navigate to flight plans list or dashboard
      navigate({
        to: `/flight-plans/$flightPlanId`,
        params: { flightPlanId: result.id },
      }); 
    
    } catch (error) {
      console.error("Failed to submit flight plan:", error);
      toast.error("Failed to submit flight plan");
    }
  };

  if (!isFlightPlanEnabled) {
    return (
      <ComingSoon
        title="Flight Planning"
        description="Flight planning tools are coming soon. Plan your routes, get approved, and fly safely."
        expectedDate="Soon"
        onNotifyMe={() => {}}
        variant="minimal"
        className="min-h-[400px] flex items-center justify-center"
      />
    );
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Create Flight Plan" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                step === "details"
                  ? setStep("draw")
                  : navigate({ to: "/" as any })
              }
              className="h-10 w-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">
                Create Flight Plan
              </h1>
              <p className="text-sm text-muted-foreground">
                Draw your flight area and submit your plan
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="w-fit">
              <MapPin className="h-3 w-3 mr-1" />
              Step {step === "draw" ? "1" : "2"} of 2
            </Badge>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-4">
          <div
            className={`flex items-center ${step === "draw" ? "text-primary" : "text-muted-foreground"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "draw" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            >
              {step === "draw" ? "1" : <CheckCircle className="h-4 w-4" />}
            </div>
            <span className="ml-2 text-sm font-medium">Draw Area</span>
          </div>
          <div className="flex-1 h-px bg-muted"></div>
          <div
            className={`flex items-center ${step === "details" ? "text-primary" : "text-muted-foreground"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "details" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            >
              2
            </div>
            <span className="ml-2 text-sm font-medium">Flight Details</span>
          </div>
        </div>

        {step === "draw" ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Draw Flight Area
                  </CardTitle>
                  <CardDescription>
                    Click on the map to create a polygon boundary for your flight
                    plan.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="show-restrictions" className="text-sm cursor-pointer">
                    Show Restrictions
                  </Label>
                  <Switch
                    id="show-restrictions"
                    checked={showFlightRestrictions}
                    onCheckedChange={(checked) => setShowFlightRestrictions(checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[600px] relative w-full overflow-hidden">
                <Map
                  id="flight-plan-map"
                  mapStyle={mapStyleUrl}
                  initialViewState={{
                    longitude: initialViewState[0],
                    latitude: initialViewState[1],
                    zoom: zoom as number,
                  }}
                  style={{ width: "100%", height: "100%" }}
                  mapLib={import("maplibre-gl")}
                  attributionControl={false}
                  reuseMaps
                >
                  <GeofenceDraw onGeofenceDrawn={handleGeofenceDrawn} />
                  {showFlightRestrictions && (
                    <FlightRestrictionsLayer />
                  )}
                </Map>
              </div>
            </CardContent>
            <div className="p-4 border-t space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {geofenceGeometry &&
                  (geofenceGeometry as GeofencePolygonGeometry).coordinates &&
                  (geofenceGeometry as GeofencePolygonGeometry).coordinates
                    .length > 0 ? (
                    <div className="flex items-center gap-2">
                      {isPolygonValid ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Area drawn successfully</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <span>Area needs adjustment</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <span>Draw an area on the map to continue</span>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleContinue}
                  disabled={!isPolygonValid}
                >
                  Continue to Details
                </Button>
              </div>
              
              {/* Area Information */}
              {geofenceGeometry &&
                (geofenceGeometry as GeofencePolygonGeometry).coordinates &&
                (geofenceGeometry as GeofencePolygonGeometry).coordinates.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                      <div>Points: {polygonPointCount - 1} (closing point excluded)</div>
                      <div>Area: {polygonAreaKm2 > 0 ? `${polygonAreaKm2.toFixed(2)} km²` : "Calculating..."}</div>
                    </div>
                    {polygonAreaKm2 > 1000 && (
                      <Alert variant="destructive" className="py-2">
                        <AlertTriangle className="h-3 w-3" />
                        <AlertDescription className="text-xs">
                          Polygon area ({polygonAreaKm2.toFixed(2)} km²) exceeds the maximum of 1000 km². Please draw a smaller area.
                        </AlertDescription>
                      </Alert>
                    )}
                    {polygonAreaKm2 > 0 && polygonAreaKm2 <= 1000 && (
                      <Alert className="py-2">
                        <CheckCircle className="h-3 w-3" />
                        <AlertDescription className="text-xs">
                          Area is within the allowed limit (max 1000 km²)
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
            </div>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Summary Card */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Area Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="w-full h-[300px] bg-muted rounded-md relative overflow-hidden">
                    <Map
                      id="preview-map"
                      mapStyle={mapStyleUrl}
                      initialViewState={{
                        longitude: initialViewState[0],
                        latitude: initialViewState[1],
                        zoom: zoom as number,
                      }}
                      style={{ width: "100%", height: "100%" }}
                      mapLib={import("maplibre-gl")}
                      attributionControl={false}
                      reuseMaps
                      interactive={true}
                    >
                      <GeofenceDraw
                        onGeofenceDrawn={() => {}}
                        initialGeometry={geofenceGeometry as GeofenceGeometry}
                        readOnly={true}
                      />
                      <FlightRestrictionsLayer  />
                    </Map>
                  </div>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setStep("draw")}
                      //   className="w-full"
                    >
                      Edit Area
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Form Card */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Flight Details</CardTitle>
                  <CardDescription>
                    Enter the details for your flight plan.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FlightPlanForm
                    onSubmit={handleSubmit}
                    polygon={
                      (geofenceGeometry as GeofencePolygonGeometry).coordinates
                    }
                    isSubmitting={createFlightPlanMutation.isPending}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
